import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SEO2025 = ({ name }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Scroll to top on mount or when name changes
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    // Initialize WOW.js for animations
    if (typeof window !== 'undefined' && window.WOW) {
      new window.WOW().init();
    }

    // Google Analytics
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-P2TS4436R5');
    }
  }, []);

  // Scroll progress + back-to-top visibility
  useEffect(() => {
    const handleScrollProgress = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 307.919 : 0;

      setScrollProgress(progress);
      setShowScroll(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScrollProgress);
    handleScrollProgress();

    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Adjust for fixed navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section with Banner Image (no video) */}
      <header className="relative w-full">
        <div
          className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-center "
          // ✅ Uses public/img/custombanner.jpg
          style={{ backgroundImage: "url('/img/custombanner.jpg')" }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-opacity-90" />

          {/* Text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center">
              SEO2025
            </h1>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-1"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>SEO2025</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100 rounded-3xl shadow-md">
        <div className="flex flex-col lg:flex-row lg:gap-12">
          {/* Main Content */}
          <article className="lg:w-3/4 space-y-12">
            <header className="text-center mb-12">
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 tracking-tight drop-shadow-md animate-fadeIn"
                data-wow-delay="0.1s"
              >
                Custom vs Prebuilt Software: What’s Right for 2025 Business?
              </h1>
              <p
                className="text-base sm:text-lg text-gray-600 mt-4 max-w-3xl mx-auto leading-relaxed animate-fadeIn"
                data-wow-delay="0.2s"
              >
                In 2025, selecting{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/our-services"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      custom vs prebuilt software
                    </a>
                  </em>
                </strong>{' '}
                for business isn't just a technical choice; it's a strategic decision that impacts your efficiency,
                scalability and growth. With digital tools driving nearly every process, businesses must choose between
                two powerful options: custom software, built specifically for their unique needs, or prebuilt software
                (ready-made or off-the-shelf solutions), designed for quick deployment. Each offers distinct advantages
                in cost, control and customization. Understanding the difference is key to choosing the{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      best software solution
                    </a>
                  </em>
                </strong>{' '}
                for your business in 2025, whether you're a startup looking to move fast or a growing company aiming for
                long-term scalability.
              </p>

              {/* Related Image Placeholder – Overview */}
              <div className="image-placeholder mt-8 flex justify-center">
                <img
                  src="/img/blog11.jpg"
                  alt="Overview of custom vs prebuilt software for 2025 business"
                />
              </div>
            </header>

            {/* What Is Custom Software */}
            <section
              id="what-is-custom"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">What Is Custom Software?</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Custom software
                    </a>
                  </em>
                </strong>{' '}
                is built from the ground up to match your business's unique goals, workflows and customer needs. Whether
                you need a CRM, dashboard, internal tool or mobile app, it gives you complete control over design,
                features, data and integrations. Unlike off-the-shelf solutions, custom software grows with your
                business, adapts to change and delivers a long-term competitive advantage. While it requires more time,
                expertise and a higher upfront investment, it offers greater flexibility, scalability and ownership,
                making it a smart choice for future-ready businesses in 2025.
              </p>

              {/* Related Image Placeholder – Custom Software */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog12.jpg"
                  alt="Custom software dashboard example"
                />
              </div>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6 mb-3">
                When to Choose Custom Software
              </h3>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>Your business has unique operations or workflows that standard tools can't support effectively.</li>
                <li>You're in a complex or high-regulation industry like healthcare, logistics, finance or SaaS.</li>
                <li>Data security, scalability and system precision are top priorities.</li>
                <li>You plan to scale rapidly and need a system that evolves with your growth.</li>
                <li>You want complete control over product features, UI/UX and software upgrades.</li>
                <li>
                  You prefer ownership over the software instead of relying on vendor policies or unpredictable monthly
                  pricing.
                </li>
                <li>
                  You want a competitive edge with custom-built functionality that no other business has in your market.
                </li>
              </ul>
            </section>

            {/* What Is Prebuilt Software */}
            <section
              id="what-is-prebuilt"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">What Is Prebuilt Software?</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Prebuilt software
                    </a>
                  </em>
                </strong>
                , also known as ready-made, packaged or off-the-shelf software, is a cost-effective solution designed
                for general business needs across multiple industries. Popular tools like Zoom, Excel, QuickBooks and
                Shopify are built to solve common problems and are easy to set up without technical expertise. Ideal for
                startups and small businesses, prebuilt software offers fast deployment, built-in support and
                budget-friendly pricing. However, its limitations include restricted customization, unused features and a
                lack of scalability. As your business grows, you may face challenges adapting it to your evolving
                workflows or end up paying for costly upgrades—or even migrating to a custom solution.
              </p>

              {/* Related Image Placeholder – Prebuilt Software */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog13.jpg"
                  alt="Examples of prebuilt business software tools"
                />
              </div>

              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mt-6 mb-3">
                When to Choose Prebuilt Software
              </h3>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-2">
                <li>You're a startup or small business needing a fast, affordable software solution.</li>
                <li>You want to avoid hiring developers or waiting months for custom development.</li>
                <li>
                  Your business operations are standard or basic, such as invoicing, team collaboration or simple CRM
                  tasks.
                </li>
                <li>You prefer tools that are easy to use and quick to set up.</li>
                <li>You need automatic updates and built-in customer support.</li>
                <li>You're working with a limited budget and want to minimize upfront costs.</li>
                <li>You're okay with limited customization in exchange for speed and simplicity.</li>
                <li>You need a temporary or short-term solution before scaling into custom software.</li>
                <li>
                  You're aware that as you grow, you might face added costs for users, integrations or advanced
                  features.
                </li>
              </ul>
            </section>

            {/* Why This Decision Matters in 2025 */}
            <section
              id="why-2025"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Why This Decision Matters in 2025
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                As we move further into a digital-first world, businesses in 2025 need software that's not just
                functional, but future-ready. Whether it's integrating AI tools, automating tasks or enhancing customer
                experience, your software will play a big role in how you operate and compete.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Choosing between custom and prebuilt software isn't just a technical decision—it's a strategic move. The
                right choice can boost productivity, reduce long-term costs and give you more flexibility in how you run
                your business.
              </p>

              {/* Related Image Placeholder – Strategy */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog14.jpg"
                  alt="Strategic decision-making for software in 2025"
                />
              </div>
            </section>

            {/* Real Business Examples */}
            <section
              id="real-examples"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Real Business Examples</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                Let's take a real-world example. A logistics company needed an advanced system to track drivers, manage
                packages, handle real-time updates and sync data across locations. No prebuilt software could offer this
                full stack of features. They decided to build{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      custom software
                    </a>
                  </em>
                </strong>
                , and although it took time, they now have a tailored solution that runs their entire business
                efficiently.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                On the other hand, a small bakery simply needed to manage inventory, handle billing and accept digital
                payments. They chose a prebuilt point-of-sale (POS) software that came with all essential features. It
                was up and running in a day and they didn't need to spend on development or a long implementation
                process.
              </p>

              {/* Related Image Placeholder – Real Examples */}
              <div className="image-placeholder my-5 flex justify-center">
                <img
                  src="/img/blog15.jpg"
                  alt="Comparison of businesses using custom vs prebuilt software"
                />
              </div>
            </section>

            {/* Conclusion */}
            <section
              id="conclusion"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">Conclusion</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                In the debate of{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/our-services"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      custom vs prebuilt software
                    </a>
                  </em>
                </strong>{' '}
                for business in 2025, there's no one-size-fits-all answer. The right choice depends on your budget,
                timeline and long-term business goals.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                If you're looking for full control, flexibility and scalability, custom software is a smart investment.
                If you need something fast, simple and affordable, prebuilt software is a great place to start.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                The most important thing is to choose software that supports your current workflow while preparing you
                for future growth. Make your decision not just based on cost, but on how the software will support your
                business success in the long run.
              </p>
            </section>

            {/* People Also Ask (PAA) */}
            <section
              id="paa"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">People Also Ask</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    1. What is the main difference between custom and prebuilt software?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Custom software is built from scratch specifically for your business, while prebuilt software is a
                    general tool created for many users with common needs.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    2. Is custom software more expensive than prebuilt software?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Yes, custom software has a higher upfront cost, but it can be more cost-effective over time. Prebuilt
                    software is cheaper initially but can become expensive as your needs grow and you require more
                    advanced features or integrations.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    3. Can I start with prebuilt software and switch to custom later?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Absolutely. Many businesses start with prebuilt tools to move quickly and control costs, then migrate
                    to custom solutions once they grow or face limitations with their existing systems.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    4. Which industries benefit most from custom software?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Industries like healthcare, logistics, e-commerce and finance often benefit from custom software due
                    to their complex, evolving and compliance-heavy requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <section
              id="call-to-action"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                Need Help Choosing the Right Software for Your Business?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                At{' '}
                <a
                  href="https://www.securxperts.com/"
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  SecurXpert Technologies
                </a>
                , we help businesses decide between{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/custom-software-development"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      custom software
                    </a>
                  </em>
                </strong>{' '}
                and ready-made tools based on their goals, workflows and growth plans. From strategy to development and
                implementation, we design solutions that align with your long-term vision.
              </p>
              <p className="text-base sm:text-lg text-gray-700 mt-4 leading-relaxed">
                Explore how we can support your journey with the right{' '}
                <strong>
                  <em>
                    <a
                      href="https://www.securxperts.com/our-services"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      custom vs prebuilt software
                    </a>
                  </em>
                </strong>{' '}
                mix tailored to your needs.
              </p>
              <div className="mt-8 flex justify-center">
                <Link
                  to="/our-services"
                  className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg"
                >
                  Talk to Our Software Experts
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/4 mt-12 lg:mt-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-8 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Quick Navigation</h3>
              <ul className="space-y-4">
                {[
                  { id: 'what-is-custom', label: 'What Is Custom Software?' },
                  { id: 'what-is-prebuilt', label: 'What Is Prebuilt Software?' },
                  { id: 'why-2025', label: 'Why It Matters in 2025' },
                  { id: 'real-examples', label: 'Real Business Examples' },
                  { id: 'conclusion', label: 'Conclusion' },
                  { id: 'paa', label: 'People Also Ask' },
                  { id: 'call-to-action', label: 'Talk to Our Experts' },
                ].map((item, index) => (
                  <li key={item.id} className="animate-slideInRight" data-wow-delay={`${0.1 * index}s`}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-blue-600 hover:text-blue-800 transition-colors text-base font-medium hover:bg-blue-100 rounded-md px-3 py-2 w-full text-left hover:shadow-md"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top with Progress Ring */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

      <style jsx>{`
        @keyframes slideInDown {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradientShift {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }
        a,
        .link {
          text-decoration: none;
        }
        .image-placeholder {
          margin: 20px 0;
          display: flex;
          justify-content: center;
        }
        .image-placeholder img {
          max-width: 500px;
          height: auto;
          object-fit: cover;
          border-radius: 10px;
        }
        @media (max-width: 768px) {
          .image-placeholder img {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default SEO2025;
